import {
  type ResponseData,
  type ApiClient,
  useApiQuery,
} from '@vrplatform/data';
import type { AdvancedDepositsModalFormInputs } from './_types';
import type { transaction_bool_exp } from '@vrplatform/graphql';
import { hasValue } from '@vrplatform/utils';
import { normalizeAdvancedDepositsSummary } from '../../views/settings/opening-balances/opening-balance-queries.utils';

type Args = {
  statementStartAt: string | null;
  teamId: string;
};

export const whereSyncedAdvancedDeposit = (
  args: Args
): transaction_bool_exp => ({
  tenantId: { _eq: args.teamId },
  date: args.statementStartAt ? { _lt: args.statementStartAt } : undefined,
  type: { _eq: 'deposit' },
  connectionId: { _is_null: false },
});

export const ADVANCED_DEPOSITS_UNIQUE_QUERY_KEY = 'advanced-deposits-modal';
export const STORYBOOK_ADVANCED_DEPOSITS_MODAL_QUERY_KEY =
  'advanced-deposits-modal';

export const formatAdvancedDepositstoFormInputs = async (
  api: ApiClient,
  advancedDeposit: ResponseData<'get:/teams/opening-balances/advanced-deposits'>
): Promise<AdvancedDepositsModalFormInputs | null> => {
  if (!advancedDeposit.transactionId) return null;

  const reservationIds = advancedDeposit.reservations
    .map((r) => r.reservationId)
    .filter(hasValue);

  const synced = !reservationIds.length
    ? []
    : await api
        .GET(
          '/teams/opening-balances/advanced-deposits/synced-payments-refunds',
          {
            params: {
              query: {
                reservationsIds: reservationIds.join(','),
              },
            },
          }
        )
        .then((res) => {
          if (res.error) throw res.error;
          return res.data.reservations;
        });

  const reservations = advancedDeposit.reservations
    .map<AdvancedDepositsModalFormInputs['reservations'][number] | null>(
      (r) => {
        const reservation = synced.find(
          (s) => s.reservationId === r.reservationId
        );

        if (!r.reservationId || !reservation) return null;

        return {
          reservationId: r.reservationId,
          paymentCentTotal: r.payment,
          refundCentTotal: r.refund,
          checkIn: reservation.checkIn,
          checkOut: reservation.checkOut,
          guestName: reservation.guestName,
          confirmationCode: reservation.confirmationCode,
          reservationCentTotal: reservation.reservationAmount ?? 0,
          synced: {
            paymentCentTotal: reservation.payment,
            refundCentTotal: reservation.refund,
            transactionIds: reservation.transactions.map(
              (t) => t.transactionId
            ),
          },
        };
      }
    )
    .filter(hasValue);

  return {
    accountId: advancedDeposit.account?.id,
    reservations,
  };
};

export function useAdvancedDepositsModalQuery() {
  return useApiQuery(
    async (api, signal): Promise<AdvancedDepositsModalFormInputs> => {
      const advancedDeposit = await api
        .GET('/teams/opening-balances/advanced-deposits', { signal })
        .then(async (res) => {
          if (res.error) throw res.error;
          return await formatAdvancedDepositstoFormInputs(
            api,
            normalizeAdvancedDepositsSummary(res.data)
          );
        });

      if (advancedDeposit) return advancedDeposit;

      return await api
        .GET(
          '/teams/opening-balances/advanced-deposits/synced-payments-refunds',
          {
            signal,
          }
        )
        .then<AdvancedDepositsModalFormInputs>((res) => {
          if (res.error) throw res.error;

          return {
            accountId: undefined,
            reservations: res.data.reservations.map((reservation) => ({
              reservationId: reservation.reservationId,
              paymentCentTotal: reservation.payment,
              refundCentTotal: reservation.refund,
              checkIn: reservation.checkIn,
              checkOut: reservation.checkOut,
              guestName: reservation.guestName,
              confirmationCode: reservation.confirmationCode,
              reservationCentTotal: reservation.reservationAmount ?? 0,
              synced: {
                paymentCentTotal: reservation.payment,
                refundCentTotal: reservation.refund,
                transactionIds: reservation.transactions.map(
                  (t) => t.transactionId
                ),
              },
            })),
          };
        });
    },
    {
      queryGroup: 'deposits',
      uniqueQueryKey: ADVANCED_DEPOSITS_UNIQUE_QUERY_KEY,
      storybookQueryKey: STORYBOOK_ADVANCED_DEPOSITS_MODAL_QUERY_KEY,
      variables: {},
      queryKeys: ['reservations'],
      keepPreviousData: true,
    }
  );
}
